import { useMemo } from "react";

import {
	ICatalogue,
	ICatalogueCustomizationsOptions,
	ICatalogueSetItem,
} from "../../apis/useGetCatalogueData/types";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { useCatalogueContext } from "../../contexts/CatalogueContext/CatalogueContext";
import { Nullable } from "../types/types";

const findCatalogueCustomizationOption = (
	itemList: ReadonlyArray<ICatalogue>,
	brandCustomizationOptionId: number
): Nullable<ICatalogueCustomizationsOptions> | undefined => {
	for (const catalogueItem of itemList) {
		if (!catalogueItem.food_supplier_customizations) continue;

		for (const cust of catalogueItem.food_supplier_customizations) {
			if (!cust?.food_supplier_customization_options) continue;

			const foundOption = cust.food_supplier_customization_options.find(
				(opt) => opt.brand_customization_option_id === brandCustomizationOptionId
			);

			if (foundOption) {
				return foundOption;
			}
		}
	}
};

const calculateCustomizationPrice = (
	customizations: ICatalogue["food_supplier_customizations"],
	itemList: ReadonlyArray<ICatalogue>
): number => {
	if (!customizations?.length || !Array.isArray(customizations)) {
		return 0;
	}

	let total = 0;

	for (const customization of customizations) {
		if (!customization?.food_supplier_customization_options) continue;

		for (const option of customization.food_supplier_customization_options) {
			if (!option) continue;

			const catalogueOption = findCatalogueCustomizationOption(
				itemList,
				option.brand_customization_option_id
			);

			if (catalogueOption) {
				total += catalogueOption.price * option.quantity;
			}
		}
	}

	return total;
};

const findProductInCatalogue = (
	itemList: ReadonlyArray<ICatalogue>,
	productId: string | number
): ICatalogue | undefined => {
	return itemList.find((item) => item.id.toString() === productId.toString());
};

const findSlotRuleDefinition = (
	catalogueProduct: ICatalogue,
	externalId: string
): Nullable<ICatalogueSetItem> => {
	if (
		!catalogueProduct.food_supplier_set_items?.length ||
		!Array.isArray(catalogueProduct.food_supplier_set_items)
	) {
		return null;
	}

	return (
		catalogueProduct.food_supplier_set_items.find((rule) => rule?.external_id === externalId) ??
		null
	);
};

const calculateSetItemOffset = (
	cartSetItem: any,
	catalogueProduct: ICatalogue,
	itemList: ReadonlyArray<ICatalogue>
): number => {
	const originalProductInSet = findProductInCatalogue(
		itemList,
		cartSetItem.food_supplier_menu_item_id
	);
	const originalItemPrice = originalProductInSet?.price ?? 0;

	if (!cartSetItem.external_id) {
		return 0;
	}

	const slotRuleDefinition = findSlotRuleDefinition(catalogueProduct, cartSetItem.external_id);

	if (!slotRuleDefinition) {
		return 0;
	}

	const defaultProductId = slotRuleDefinition.food_supplier_menu_item_id;
	const defaultProduct = findProductInCatalogue(itemList, defaultProductId);
	const defaultProductPrice = defaultProduct?.price ?? 0;

	const customize = slotRuleDefinition.customize;
	const basePrice = slotRuleDefinition.base_price ?? 0;
	const pegBasePrice = slotRuleDefinition.peg_base_price;

	if (pegBasePrice || !customize) {
		return Math.max(0, originalItemPrice - defaultProductPrice);
	}

	return Math.max(0, originalItemPrice - basePrice);
};

const calculateSetItemsPrice = (
	setItems: ICatalogue["food_supplier_set_items"],
	catalogueProduct: ICatalogue,
	itemList: ReadonlyArray<ICatalogue>
): number => {
	if (!setItems?.length || !Array.isArray(setItems)) {
		return 0;
	}

	let total = 0;

	for (const cartSetItem of setItems) {
		if (!cartSetItem) continue;
		const setItemOffset = calculateSetItemOffset(cartSetItem, catalogueProduct, itemList);

		total += setItemOffset * cartSetItem.quantity;

		const customizationPrice = calculateCustomizationPrice(
			cartSetItem.food_supplier_customizations,
			itemList
		);

		total += customizationPrice;
	}

	return total;
};

const calculateItemTotal = (
	cartItem: ICatalogue,
	catalogueProduct: ICatalogue,
	itemList: ReadonlyArray<ICatalogue>,
	isTakeaway: boolean
): number => {
	let itemTotal = catalogueProduct.price;

	const customizationPrice = calculateCustomizationPrice(
		cartItem.food_supplier_customizations,
		itemList
	);

	itemTotal += customizationPrice;

	const setItemsPrice = calculateSetItemsPrice(
		cartItem.food_supplier_set_items,
		catalogueProduct,
		itemList
	);

	itemTotal += setItemsPrice;

	if (isTakeaway && catalogueProduct.takeaway_fee) {
		itemTotal += catalogueProduct.takeaway_fee;
	}

	return itemTotal * cartItem.quantity;
};

const validateInputs = (
	itemList: ReadonlyArray<ICatalogue>,
	selections: Nullable<ICatalogue>[]
): ICatalogue[] => {
	if (itemList.length === 0) {
		return [];
	}

	if (!selections || selections.length === 0) {
		return [];
	}

	const validSelections = selections.filter(
		(item): item is ICatalogue => item !== null && item !== undefined
	);

	return validSelections;
};

export const useItemSubtotal = (selections: Nullable<ICatalogue>[]): number => {
	const { state } = useCatalogueContext((contextState) => contextState.products);
	const { state: isTakeaway } = useCartContext((contextState) => contextState.is_takeaway);
	const itemList: ReadonlyArray<ICatalogue> = Array.isArray(state) ? state : [];

	const subtotal = useMemo(() => {
		const validSelections = validateInputs(itemList, selections);

		if (validSelections.length === 0) {
			return 0;
		}

		let calculatedSubtotal = 0;

		for (const cartItem of validSelections) {
			const catalogueProduct = itemList.find(
				(item) => item.brand_menu_item_id === cartItem.brand_menu_item_id
			);

			if (!catalogueProduct) {
				continue;
			}

			const itemTotal = calculateItemTotal(cartItem, catalogueProduct, itemList, isTakeaway);

			calculatedSubtotal += itemTotal;
		}

		return calculatedSubtotal;
	}, [selections, itemList, isTakeaway]);

	return subtotal;
};
